import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CloseCircleFilled } from "@ant-design/icons";
import { Button, Checkbox, Input, Modal, message } from "antd";
import { LOGIN_LEGAL_AGREE_PROMPT } from "../../components/legal/LegalAgreePromptContent";
import { useAgreeGate } from "../../components/legal/UseAgreeGate";
import { GlobalApi } from "../../global/GlobalApi";
import { useLongfansMock } from "../../global/GlobalMock";
import type { UserInfo } from "../../global/GlobalType";
import { openLoginCaptcha } from "../../utils/tianaiCaptcha";
import "../../platform_spaces/home/LoginModal.css";

type Props = {
  open: boolean;
  onCancel: () => void;
  onLoginSuccess: (user: UserInfo) => void;
};

const CODE_SECONDS = 60;
const API_BASE = () => process.env.REACT_APP_API_DOMAIN?.trim() ?? "";

export function TactikLoginModal(props: Props) {
  const { open, onCancel, onLoginSuccess } = props;
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const { runWithAgree, agreePromptModal } = useAgreeGate({
    agreed,
    setAgreed,
    uncheckedTip: LOGIN_LEGAL_AGREE_PROMPT.uncheckedTip,
  });

  useEffect(() => {
    if (!open) {
      setPhone("");
      setCode("");
      setCooldown(0);
      setAgreed(false);
    }
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const t = window.setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  const doSendCode = useCallback(
    async (captchaToken?: string) => {
      setSending(true);
      try {
        const ok = await GlobalApi.sendLoginVerificationCode(phone, captchaToken);
        if (!ok) {
          return;
        }
        message.success("验证码已发送");
        setCode("");
        setCooldown(CODE_SECONDS);
      } finally {
        setSending(false);
      }
    },
    [phone],
  );

  const proceedSendCode = useCallback(async () => {
    if (!/^\d{11}$/.test(phone)) {
      message.warning("请输入11位手机号");
      return;
    }
    if (cooldown > 0 || sending) {
      return;
    }
    if (useLongfansMock) {
      await doSendCode();
      return;
    }
    const base = API_BASE();
    try {
      await openLoginCaptcha({
        genUrl: `${base}/lark/app/genCaptcha`,
        checkUrl: `${base}/lark/app/check`,
        onSuccess: (token) => doSendCode(token),
        onFail: () => message.error("滑动验证失败，请重试"),
      });
    } catch {
      message.error("验证码组件加载失败");
    }
  }, [phone, cooldown, sending, doSendCode]);

  const onSendCodeClick = useCallback(() => {
    runWithAgree(() => void proceedSendCode(), LOGIN_LEGAL_AGREE_PROMPT);
  }, [runWithAgree, proceedSendCode]);

  const onSubmit = useCallback(async () => {
    if (!/^\d{11}$/.test(phone)) {
      message.warning("请输入11位手机号");
      return;
    }
    if (!/^\d{4,6}$/.test(code.trim())) {
      message.warning("请输入短信验证码");
      return;
    }
    setSubmitting(true);
    try {
      const user = await GlobalApi.loginByPhoneCode(phone, code.trim());
      if (!user) {
        message.error("验证码错误，请重试");
        return;
      }
      message.success("登录成功");
      onLoginSuccess(user);
    } finally {
      setSubmitting(false);
    }
  }, [phone, code, onLoginSuccess]);

  const onSubmitClick = useCallback(() => {
    runWithAgree(() => void onSubmit(), LOGIN_LEGAL_AGREE_PROMPT);
  }, [runWithAgree, onSubmit]);

  return (
    <>
      {agreePromptModal}
      <Modal
        className="lf-login-modal"
        title={null}
        open={open}
        onCancel={onCancel}
        footer={null}
        width={400}
        centered
        destroyOnClose
        closable
      >
        <div className="lf-login-head">
          <h2 className="lf-login-title">登录 Tactik</h2>
          <p className="lf-login-sub">知其所以，方能长久。</p>
        </div>
        <div className="lf-login-fields">
          <Input
            className="lf-login-input"
            size="large"
            prefix={<span className="lf-login-phone-prefix">+86</span>}
            placeholder="手机号"
            maxLength={11}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
            inputMode="numeric"
            autoComplete="tel"
          />
          <div className="lf-login-code-row">
            <Input
              className="lf-login-input lf-login-input--code"
              size="large"
              placeholder="短信验证码"
              maxLength={6}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              suffix={
                code ? (
                  <CloseCircleFilled
                    className="lf-login-code-clear"
                    role="button"
                    aria-label="清除"
                    onClick={() => setCode("")}
                  />
                ) : null
              }
              inputMode="numeric"
              autoComplete="one-time-code"
            />
            <Button
              className={`lf-login-btn-secondary longfans-rect-sm${!agreed ? " lf-btn-need-agree" : ""}`}
              disabled={cooldown > 0}
              loading={sending}
              onClick={onSendCodeClick}
            >
              {cooldown > 0 ? `${cooldown}s` : "获取验证码"}
            </Button>
          </div>
          <p className="lf-login-hint">首次登录即注册</p>
          <Checkbox
            className="lf-login-agree"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          >
            <span>
              我已阅读并同意
              <Link
                className="lf-login-agree-link"
                to="/legal/user-agreement"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                《用户协议》
              </Link>
              与
              <Link
                className="lf-login-agree-link"
                to="/legal/privacy"
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                《隐私政策》
              </Link>
            </span>
          </Checkbox>
        </div>
        <div className="lf-login-actions">
          <Button
            type="primary"
            className={`lf-login-btn-ok${!agreed ? " lf-btn-need-agree" : ""}`}
            block
            loading={submitting}
            onClick={onSubmitClick}
          >
            登录
          </Button>
        </div>
      </Modal>
    </>
  );
}
